<template>
  <form
    id="autocomplete"
    :action="submitAction"
    @submit="onSubmitFormActions($event)"
    @click="handleFormClick"
    data-testid="sbx-sc__autocomplete-form"
  >
    <div class="search-bar" ref="searchBar">
      <input
        autocomplete="off"
        name="text"
        type="text"
        class="search-bar__text"
        :placeholder="placeholder"
        :value="searchText"
        @input="ev => (searchText = ev.target.value)"
        @focus="handleFocus"
        @blur="handleBlur"
      />
      <button type="submit" class="search-bar__submit">
        <icon :name="iconClass" />
      </button>
    </div>
    <autocomplete-suggestions
      v-if="shouldDisplayResults && !this.searchTextLength"
      :ourSelection="ourSelectionItems"
      :popularSearches="popularSearchesItems"
      :locationSuggestions="locationSuggestions"
      :productSuggestions="productSuggestions"
      :searchText="searchText"
      :submitAction="submitAction"
      :context="context"
      :searchByLocationEnabled="searchByLocationEnabled"
    />
  </form>
</template>

<script>
import Icon from '@/atoms/Icon.vue';
import AutocompleteSuggestions from './AutocompleteSuggestions.vue';
import { autocomplete } from './index';
import { accentInsensitiveRegex } from '@/utils/text';
import { withContext } from '@/utils/mixins/withContext';
import { getRedirectMappings, getPopularSearches } from '@/api/autocomplete';
import { to } from '@/utils';
import { fetchSearchResults } from '@/api/autocomplete';
import { fetchFeaturesData } from '@/api/features';
import { debounce } from '@/utils/debouncer';
import { dispatchEvent } from '@/utils/events';
import { autocompleteBlurEvent, autocompleteFocusEvent } from '@/utils/constants/customEventNames';
import { limitArrayToLength } from '@/utils/arrays';
import { handleSearchResultTracking } from './AutocompleteTrackingHandler';

export default {
  components: { Icon, AutocompleteSuggestions },
  props: {
    ...autocomplete.propTypes,
    searchResultsOverride: {
      type: Object,
    },
  },
  mixins: [withContext],
  data() {
    return {
      searchText: '',
      shouldDisplayResults: false,
      locations: [],
      products: [],
      popularSearches: [],
      redirectMappings: [],
      searchByLocationEnabled: false,
    };
  },
  async mounted() {
    await this.loadRedirectMappings();
    await this.loadPopularSearches();
    await this.fetchFeatures();
    window.addEventListener('click', this.handleWindowClick);
    window.addEventListener('click-on-result-item', this.closeSuggestions);
  },
  destroyed() {
    window.removeEventListener('click', this.handleWindowClick);
    window.removeEventListener('click-on-result-item', this.closeSuggestions);
  },

  methods: {
    async fetchFeatures() {
      const [error, data] = await to(fetchFeaturesData());

      if (error || !data) {
        this.searchByLocationEnabled = false;
        console.error(error);
        return;
      }
      this.searchByLocationEnabled = data.sbl.enabled;
    },
    onSubmitFormActions(event) {
      // Do not send empty inputs when the form is submitted
      event.target.addEventListener('formdata', function(event) {
        const formData = event.formData;
        for (const [name, value] of Array.from(formData.entries())) {
          if (value === '') formData.delete(name);
        }
      });

      handleSearchResultTracking('search results shown', 'show results', {
        searchText: this.searchText,
        ourSelectionItems: this.ourSelectionItems,
        locationSuggestions: this.locationSuggestions,
        productSuggestions: this.productSuggestions,
        popularSearches: this.popularSearchesItems.map(popularSearch => popularSearch.title),
        translate: t => this.$t(t),
      });
    },
    async search() {
      const [error, data] = await to(fetchSearchResults(this.searchText, this.searchByLocationEnabled));

      if (error) {
        console.error(error);
        return {};
      }

      return data || {};
    },
    mapLocationData(category, items = []) {
      return items.map(({ label, ...item }) => ({ label, category, ...item }));
    },
    handleFormClick(ev) {
      ev.stopPropagation();
    },
    async loadRedirectMappings() {
      const [error, data] = await to(getRedirectMappings(this.context.brand, this.context.locale));
      if (error) {
        this.redirectMappings = [];
        return;
      }

      this.redirectMappings = data;
    },
    async loadPopularSearches() {
      const { brand, locale } = this.context;
      const [error, data] = await to(getPopularSearches(brand, locale));

      if (error) {
        this.popularSearches = [];
        return;
      }

      this.popularSearches = data;
    },
    handleFocus() {
      dispatchEvent(autocompleteFocusEvent);
    },
    handleBlur() {
      window.dispatchEvent(new CustomEvent(autocompleteBlurEvent));
    },
    handleWindowClick() {
      if (this.shouldDisplayResults) {
        this.shouldDisplayResults = false;
      }
    },
    closeSuggestions() {
      this.searchText = '';
      this.shouldDisplayResults = false;
    },
  },
  watch: {
    shouldDisplayResults() {
      if (this.shouldDisplayResults && !this.searchTextLength) {
        document.body.classList.add('search-result-open');
      } else {
        document.body.classList.remove('search-result-open');
      }
    },

    searchText: debounce(async function() {
      // Do not let empty spaces to be entered at the begining.
      this.searchText = this.searchText.trimStart();

      if (this.searchTextLength) {
        this.shouldDisplayResults = false;
        return;
      }

      const { locations, boxes } = await this.search();
      // const cityData = this.mapLocationData(locationCategories.city, city);
      // const regionData = this.mapLocationData(locationCategories.region, region);
      // const subRegionData = this.mapLocationData(locationCategories.subRegion, sub_region);

      // this.locations = [...cityData, ...regionData, ...subRegionData];
      this.locations = locations;
      this.products = boxes;
      this.shouldDisplayResults = !!(boxes || locations || this.ourSelectionItems.length || this.popularSearchesItems.length);
    }),
    async context() {
      await this.loadRedirectMappings();
      await this.loadPopularSearches();
    },
  },
  computed: {
    searchTextLength() {
      const searchTextTrimmed = this.searchText.trim();
      return searchTextTrimmed.length < 3;
    },
    popularSearchesItems() {
      if (!this.isPopularSearchesDisplayed || this.searchTextLength) {
        return [];
      }
      return limitArrayToLength(this.popularSearches, this.maxPopularSearchesItems);
    },
    ourSelectionItems() {
      if (!this.redirectMappings || !this.shouldDisplayResults) return [];
      const accentsRegex = accentInsensitiveRegex(this.searchText, true);
      const ourSelection = this.redirectMappings.filter(({ phrase }) => {
        // need to reset the last index for the regex
        // ref: https://geekingfrog.com/blog/post/reuse-javascript-regexp-global-flag-gotcha
        accentsRegex.lastIndex = 0;
        return accentsRegex.test(phrase);
      });

      ourSelection.length = Math.max(0, Math.min(ourSelection.length, this.maxOurSelectionItems));
      return ourSelection;
    },
    isPopularSearchesDisplayed() {
      return !this.locationSuggestions.length && !this.ourSelectionItems.length;
    },
    productSuggestions() {
      return limitArrayToLength(this.products, this.maxProductItems);
    },
    locationSuggestions() {
      return limitArrayToLength(this.locations, this.maxLocationItems);
    },
    iconClass() {
      return this.context.isPrimaryBrand ? 'search-2' : 'search';
    },
  },
};
</script>

<style scoped lang="scss">
@import '@/scss/smartbox-icons.scss';
@import './Autocomplete.brands.scss';

$search-height-primary: rem-calc(40);
$search-height-primary-mobile: rem-calc(40);
$search-border-color: #c6c3bd;
$search-text-color-primary: #343533;

#autocomplete {
  position: relative;
  z-index: 99;

  .search-bar {
    overflow: hidden;
    justify-content: space-between;
    position: relative;
    height: $search-height-primary-mobile;
    border-radius: calc(#{$search-height-primary-mobile} / 2);
    background-color: white;
    border: 1px solid $search-border-color;
    transition: box-shadow 0.2s;

    @media #{$medium-up} {
      height: $search-height-primary;
      border-radius: calc(#{$search-height-primary} / 2);

      &:hover {
        box-shadow: $search-box-shadow;
        border: 1px solid white;
      }
    }

    &__text {
      width: calc(100% - #{rem-calc(54)});
      height: 100%;
      border: none;
      font-size: rem-calc(12);
      line-height: rem-calc(22);
      padding: rem-calc(9 8 9 24);
      color: $search-text-color-primary;
      background-color: transparent;
      font-weight: 500;

      &::placeholder {
        transition: 0.3s;
        color: $search-text-color-primary;
      }

      &:focus {
        outline: none;

        &::placeholder {
          color: transparent;
        }
      }

      @media screen and (-webkit-min-device-pixel-ratio: 0) and (max-device-width: 1024px) {
        font-size: 1rem;
      }
    }

    &__submit {
      position: absolute;
      right: rem-calc(8);
      padding: rem-calc(4 8);
      top: 0;
      width: rem-calc(42);
      height: 100%;
      color: $cta-color;
      background-color: transparent;
      font-size: rem-calc(20);
      text-align: center;
      cursor: pointer;
      border: none;
      outline: none;
    }
  }
}
</style>
